//frontend/src/components/SearchManagerRequests/SearchManagerRequestDetail.jsx

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Chat from "./Chat";

import {
  getComments,
  getCurrentUser,
  getDocuments,
  getEvents,
  getRequestSelection,
} from "../api/requestSelection/requestSelectionApi";

import { StatusBadge, fmtDate, fmtDateOnly } from "./constants";
import "./SearchManagerRequests.css";

export default function SearchManagerRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const requestId = id;
  const onBack = () => navigate("/procurement/requests");

  const [request, setRequest] = useState(null);
  const [comments, setComments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [events, setEvents] = useState([]);
  const [me, setMe] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [req, cmts, docs, evs, user] = await Promise.all([
          getRequestSelection(requestId),
          getComments(requestId),
          getDocuments(requestId),
          getEvents(requestId),
          getCurrentUser(),
        ]);
        setRequest(req);
        setComments(cmts);
        setDocuments(docs);
        setEvents(evs);
        setMe(user);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    if (requestId) load();
  }, [requestId]);

  if (loading) {
    return (
      <div className="proc-page">
        <div className="proc-empty proc-empty--large">
          <h3>Загрузка заявки...</h3>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="proc-page">
        <button className="proc-back-button" onClick={onBack}>
          ← Мои заявки
        </button>
        <div className="proc-empty proc-empty--large">
          <div className="proc-empty__icon">⚠️</div>
          <h3>{error}</h3>
        </div>
      </div>
    );
  }

  if (!request) return null;

  return (
    <div className="proc-page">
      <div className="proc-page__header">
        <div>
          <button className="proc-back-button" onClick={onBack}>
            ← Мои заявки
          </button>
          <div className="proc-request-heading">
            <div>
              <div className="proc-request-id">{request.number}</div>
              <h1>{request.title || `Заявка ${request.number}`}</h1>
            </div>
            <StatusBadge status={request.status} />
          </div>
        </div>
      </div>

      <main>
        <section className="proc-section">
          <div className="proc-section__title">
            <div>
              <h2>Информация о заявке</h2>
              <span>Основные данные</span>
            </div>
          </div>
          <div className="proc-description">
            <p>
              <strong>Номер:</strong> {request.number}
            </p>
            <p>
              <strong>Товар:</strong>{" "}
              {request.title || <span className="proc-muted">не задан</span>}
            </p>
            <p>
              <strong>Клиент:</strong>{" "}
              {request.client_name || (
                <span className="proc-muted">не указан</span>
              )}
            </p>
            <p>
              <strong>Администратор:</strong>{" "}
              {request.client_manager_name ||
                `#${request.client_manager_id}`}
            </p>
            <p>
              <strong>Менеджер по поиску:</strong>{" "}
              {request.search_manager_name ||
                (request.search_manager_id
                  ? `#${request.search_manager_id}`
                  : "не назначен")}
            </p>
            <p>
              <strong>Создана:</strong> {fmtDate(request.created_at)}
            </p>
            <p>
              <strong>Обновлена:</strong> {fmtDate(request.updated_at)}
            </p>
          </div>
        </section>

        <section className="proc-section">
          <div className="proc-section__title">
            <div>
              <h2>Требования клиента</h2>
              <span>Описание задачи</span>
            </div>
          </div>
          <div className="proc-description">
            {request.description || "Описание не указано."}
          </div>
        </section>

        <section className="proc-section">
          <div className="proc-section__title">
            <div>
              <h2>Документы</h2>
              <span>{documents.length} файлов</span>
            </div>
          </div>
          <div className="proc-description">
            {documents.length === 0 && (
              <div className="proc-empty">Документов нет</div>
            )}
            {documents.map((doc) => (
              <div
                key={doc.id}
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                  padding: 10,
                  marginBottom: 8,
                  background: "#f9fafb",
                  borderRadius: 8,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>
                    📎 {doc.name || "файл"}
                  </div>
                  {doc.comment && (
                    <div
                      style={{ color: "#6b7280", marginTop: 4, fontSize: 13 }}
                    >
                      {doc.comment}
                    </div>
                  )}
                  <div
                    style={{ color: "#9ca3af", marginTop: 4, fontSize: 11 }}
                  >
                    {fmtDate(doc.created_at)}
                  </div>
                </div>
                <a
                  href={
                    doc.url && doc.url.startsWith("http")
                      ? doc.url
                      : `${process.env.REACT_APP_DJANGO_BACKEND}${doc.url}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="proc-button proc-button--secondary"
                  style={{ textDecoration: "none", alignSelf: "center" }}
                >
                  ↓ Скачать
                </a>
              </div>
            ))}
          </div>
        </section>

        <section className="proc-section">
          <div className="proc-section__title">
            <div>
              <h2>Комментарии</h2>
              <span>{comments.length}</span>
            </div>
          </div>
          <div className="proc-description">
            {comments.length === 0 && (
              <div className="proc-empty">Комментариев нет</div>
            )}
            {comments.map((c) => (
              <div
                key={c.id}
                style={{
                  padding: 10,
                  marginBottom: 8,
                  background: "#f9fafb",
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <strong>{c.author_name}</strong>
                  <span style={{ color: "#9ca3af", fontSize: 11 }}>
                    {fmtDate(c.created_at)}
                  </span>
                </div>
                <div style={{ marginTop: 6 }}>{c.text}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="proc-section">
          <div className="proc-section__title">
            <div>
              <h2>События</h2>
              <span>{events.length}</span>
            </div>
          </div>
          <div className="proc-description">
            {events.length === 0 && (
              <div className="proc-empty">Событий нет</div>
            )}
            {events.map((ev) => (
              <div
                key={ev.id}
                style={{
                  padding: 10,
                  marginBottom: 8,
                  background: "#f9fafb",
                  borderRadius: 8,
                  borderLeft: `4px solid ${ev.color || "#3B82F6"}`,
                }}
              >
                <div style={{ fontWeight: 600 }}>{ev.title}</div>
                <div
                  style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}
                >
                  {fmtDateOnly(ev.start_time || ev.start_at)} —{" "}
                  {fmtDateOnly(ev.end_time || ev.end_at)}
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      {me && request?.matrix_room_id && (
        <Chat
          matrixRoomId={request.matrix_room_id}
          matrixUserId={me.matrix?.user_id}
          accessToken={me.matrix?.access_token}
        />
      )}
    </div>
  );
}